import { EditOutlined, EyeFilled } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { Button, Tooltip, Typography } from 'antd';
import moment from 'moment';
import { ArrowClockwise, PencilSimpleLine } from 'phosphor-react';
import React, { useCallback, useMemo, useState } from 'react';
import videoPlaceholder from '../../assets/images/imagePlaceholder.png';
import {
  CLOUD_FRONT_CACHE_KEYS,
  LIST_TYPES,
  LIVE_STATUS_COLORS,
  MODULES,
  PLAN_TYPE,
  ROUTES,
  VIDEO_LIVE_STATUS_OPTIONS,
  WORKSPACE_ROLE_LEVEL,
  WORKSPACE_ROLE_PERMISSION
} from '../../common/constants';
import { getImageUrl } from '../../common/utils';
import FilterBar, { useFilterBar } from '../../components/FilterBar';
import Image from '../../components/Image';
import PageHeader from '../../components/PageHeader';
import PageList from '../../components/PageList';
import useCheckPermission from '../../hooks/useCheckPermission';
import AssignPlanModal from '../component/AssignPlanModal';
import CustomCard from '../component/card/Cscard';
import { INVALIDATE_CLOUD_FRONT_CACHE } from '../menus/graphql/Mutations';
import { GET_SUBSCRIPTION_PLANS } from '../subscriptions-plans/graphql/Queries';
import { Select, useModal } from './components/FormInputs';
import { BULK_EDIT_VIDEOS } from './graphql/Mutations';
import { GET_VIDEOS_MODULES } from './graphql/Queries';

const VideoCard = ({
  createdAt,
  id,
  title,
  description,
  imageThumbnail,
  onEdit,
  onPreview,
  isAssetAllowed,
  isLabelAllowed,
  isViewOnly,
  isLive,
  liveStatus
}) => {
  return (
    <CustomCard
      isLive={isLive}
      onClick={onPreview}
      status={
        isLive
          ? VIDEO_LIVE_STATUS_OPTIONS.find(
              (status) => status.value === liveStatus
            )?.label
          : undefined
      }
      statusColor={isLive ? LIVE_STATUS_COLORS[liveStatus] : undefined}
      alt={title}
      src={getImageUrl(imageThumbnail?.url) ?? videoPlaceholder}
      blurHash={imageThumbnail?.blurhash}
      title={moment(createdAt).format('ll')}
      heading={title}
      description={description}
      primaryOnClick={() => onEdit(id)}
      primary={
        isAssetAllowed &&
        isLabelAllowed &&
        (isViewOnly ? (
          <>
            <EyeFilled size={16} />
            View
          </>
        ) : (
          <>
            <PencilSimpleLine size={16} />
            Edit
          </>
        ))
      }
    />
  );
};

const getColumns = ({
  handleEdit: onEdit,
  isAssetAllowed,
  isLabelAllowed,
  isViewOnly
}) => [
  {
    title: 'Video',
    dataIndex: 'imageThumbnail',
    ellipsis: true,
    render: (value, { title }) => (
      <Image
        className="group-img-table"
        blurHash={value?.blurhash}
        src={
          getImageUrl(value?.url, { height: 50, width: 80 }) ?? videoPlaceholder
        }
        alt={title}
      />
    )
  },
  {
    title: 'Title',
    dataIndex: 'title',
    ellipsis: true
  },
  {
    title: 'Description',
    dataIndex: 'description',
    ellipsis: true
  },
  ...(isAssetAllowed && isLabelAllowed
    ? [
        {
          title: 'Action',
          dataIndex: 'id',
          render: (value) => (
            <Tooltip title={`${isViewOnly ? 'View' : 'Edit'} Video`}>
              <Button
                type="text"
                className="text-btn"
                onClick={() => onEdit(value)}
              >
                {isViewOnly ? <EyeFilled /> : <EditOutlined />}
              </Button>
            </Tooltip>
          )
        }
      ]
    : [])
];

const variablesSelector = ({
  limit,
  offset,
  search,
  sortField,
  sortOrder,
  allowedPlans,
  isLive
}) => ({
  filter: {
    skip: offset,
    search,
    limit,
    allowedPlans,
    isLive
  },
  sort: {
    sortOn: sortField,
    sortBy: sortOrder
  }
});

const dataSelector = ({ videosAdmin }) => ({
  data: videosAdmin?.videos ?? [],
  count: videosAdmin?.count ?? 0
});

const Videos = ({ history }) => {
  const filterProps = useFilterBar();
  const { open, openModal, closeModal } = useModal();

  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [selectedVideoIds, setSelectedVideoIds] = useState([]);
  const [selectedPlanKey, setSelectedPlanKey] = useState([]);

  const [invalidateCache, { loading: isInvalidating }] = useMutation(
    INVALIDATE_CLOUD_FRONT_CACHE,
    {
      variables: { key: CLOUD_FRONT_CACHE_KEYS.OG_IMAGES }
    }
  );

  const [bulkEditVideoMutatuion, { loading: submitLoading }] = useMutation(
    BULK_EDIT_VIDEOS,
    {
      onError() {},
      onCompleted() {
        setSelectedPlanKey([]);
        setSelectedVideoIds([]);
        closeModal();
      }
    }
  );

  const isAddEditAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.CONTENT_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const isViewOnly = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.CONTENT_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.VIEW]
    }
  ]);

  const isLabelAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.LABEL_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.VIEW,
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const isAssetAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.ASSET_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.VIEW,
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const handleShowModal = () => {
    history.push(`${ROUTES?.VIDEOS_MODULE}/add`);
  };

  const handleEdit = useCallback((id) => {
    history.push(`${ROUTES?.VIDEOS_MODULE}/${id}/edit`);
  }, []);

  const columns = useMemo(
    () =>
      getColumns({
        handleEdit,
        isAddEditAllowed,
        isAssetAllowed,
        isLabelAllowed,
        isViewOnly
      }),
    [handleEdit]
  );

  const handleBulkEdit = () => {
    setIsBulkEdit(true);
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedVideoIds(newSelectedRowKeys);
  };

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedVideoIds,
      onChange: onSelectChange
    }),
    [selectedVideoIds]
  );

  const handlePlanSelection = (plans) => {
    const arrayOfkeys = plans?.map((plan) => plan?.key);
    setSelectedVideoIds([]);
    setSelectedPlanKey(arrayOfkeys);
  };

  const queryFilters = useMemo(() => {
    return {
      ...filterProps.filters,
      ...(isBulkEdit &&
        selectedPlanKey?.length > 0 && {
          allowedPlans: selectedPlanKey
        })
    };
  }, [filterProps.filters, isBulkEdit, selectedPlanKey]);

  return (
    <>
      <AssignPlanModal
        open={open}
        onClose={closeModal}
        selectedPlanKey={selectedPlanKey}
        selectedIds={selectedVideoIds}
        bulkEditMutatuion={bulkEditVideoMutatuion}
        submitLoading={submitLoading}
      />
      <PageHeader
        menu={MODULES?.CONTENT_UNITS}
        handleShowModal={handleShowModal}
        isAddEditAllowed={isAddEditAllowed && isAssetAllowed && isLabelAllowed}
        isBulkEditAllowed
        bulkEditDisabled={
          isBulkEdit &&
          selectedPlanKey?.length <= 0 &&
          selectedVideoIds?.length <= 0
        }
        isBulkEdit={isBulkEdit}
        handleBulkEdit={handleBulkEdit}
        openModal={openModal}
        selectedPlanKey={selectedPlanKey}
        selectedIds={selectedVideoIds}
      />
      <FilterBar
        {...filterProps}
        {...(isBulkEdit && {
          show: {
            sort: false,
            listModes: false
          }
        })}
        SearchProps={{ placeholder: 'Search Videos' }}
        extraFilters={
          isBulkEdit && (
            <>
              <Typography.Text className="sort-by-title" type="secondary">
                Select Plan
              </Typography.Text>
              <Select
                multiple
                popupMatchSelectWidth={false}
                excludeOptions={[PLAN_TYPE.GUEST, PLAN_TYPE.FREE]}
                placeholder="Select plan"
                query={GET_SUBSCRIPTION_PLANS}
                variablesSelector={(filter) => ({
                  filter: {
                    isActive: true,
                    ...filter
                  }
                })}
                allowClear
                dataSelector={(res) =>
                  res?.subscriptionPlans?.subscriptionPlans?.map(({ key }) => ({
                    label: key,
                    value: key
                  })) ?? {}
                }
                keys={{
                  data: 'subscriptionPlans',
                  records: 'subscriptionPlans',
                  count: 'count'
                }}
                onChange={handlePlanSelection}
                maxTagCount="responsive"
                className="plan-selection"
              />
            </>
          )
        }
      >
        <Tooltip title="Revalidate OG images" placement="left">
          <Button
            className="text-btn d-flex align-center justify-center"
            type="text"
            icon={<ArrowClockwise size={24} />}
            onClick={invalidateCache}
            loading={isInvalidating}
          />
        </Tooltip>
      </FilterBar>
      <PageList
        filters={queryFilters}
        listMode={isBulkEdit ? LIST_TYPES.TABLE : filterProps.listMode}
        query={GET_VIDEOS_MODULES}
        variablesSelector={variablesSelector}
        dataSelector={dataSelector}
        GridProps={{
          renderItem: (props) => (
            <VideoCard
              {...props}
              onEdit={handleEdit}
              isAddEditAllowed={isAddEditAllowed}
              isAssetAllowed={isAssetAllowed}
              isLabelAllowed={isLabelAllowed}
              isViewOnly={isViewOnly}
            />
          )
        }}
        TableProps={{
          columns,
          ...(isBulkEdit && selectedPlanKey?.length <= 0 && { rowSelection })
        }}
      />
    </>
  );
};

export default Videos;
